/**
 * @file 时间格式转换相关
 */

import { commonLang, _system, isLang } from '@umu/ukit-lang';
import { pad0 } from '../string';

export function getLocaleDateFormat() {
    let dateString = 'MM/dd/yyyy';

    if (_system('CN')) {
        dateString = isLang('CN') || isLang('TW') ? 'yyyy年MM月dd日' : 'yyyy/MM/dd';
    } else if (_system('JP')) {
        dateString = 'yyyy/MM/dd';
    }

    return dateString;
}

export function getLocaleDateFormatForMD() {
    let dateString = 'MM/dd';

    if (_system('CN')) {
        dateString = isLang('CN') || isLang('TW') ? 'MM月dd日' : 'MM/dd';
    } else if (_system('JP')) {
        dateString = 'MM/dd';
    }

    return dateString;
}

export function getLocaleDateTimeFormat() {
    let dateString = 'MM/dd/yyyy hh:mm';

    if (_system('CN')) {
        dateString = 'yyyy-MM-dd hh:mm';
    } else if (_system('JP')) {
        dateString = 'yyyy/MM/dd hh:mm';
    }

    return dateString;
}

/**
 * 日期格式化
 * @param timeStamp
 * @param format yyyy-MM-dd
 */
export const customFormatDateTime = (timeStamp: number | Date, format: string = 'yyyy-MM-dd') => {
    interface IDateMap {
        yyyy: string;
        yy: string;
        MM: string;
        M: string;
        dd: string;
        d: string;
        hh: string;
        h: string;
        m: string;
        mm: string;
        s: string;
        ss: string;
        ms: string;
    }

    const DATE_REG = /\byyyy|yy|MM|M|dd|d|hh|h|mm|ms|ss|m|s\b/g;
    const time = timeStamp instanceof Date ? timeStamp : new Date(timeStamp);
    const yyyy = String(time.getFullYear());
    const yy = yyyy.substr(2);
    const M = String(time.getMonth() + 1);
    const MM = pad0(M);
    const d = String(time.getDate());
    const dd = pad0(d);
    const h = String(time.getHours());
    const hh = pad0(h);
    const m = String(time.getMinutes());
    const mm = pad0(m);
    const s = String(time.getSeconds());
    const ss = pad0(s);
    const ms = String(time.getMilliseconds());

    const map: IDateMap = { yyyy, yy, MM, M, dd, d, hh, h, m, mm, s, ss, ms };
    return format.replace(DATE_REG, ($1) => {
        const key = $1 as keyof IDateMap;
        return map[key];
    });
};

/**
 * 将seconds转换为本地时间，默认转换为 00:00 格式
 * 将第二个参数设置为 true 时为 00:00:00 格式
 */
export function convertSeconds(seconds: number | string, format?: string) {
    if (!format) {
        format = 'mm:ss';
        // 大于一小时
        if (Number(seconds) > 60 * 60) {
            format = 'hh:mm:ss';
        }
    }
    // 减去时区对应的秒数，以用通用的方法格式化
    const num = (+seconds + new Date().getTimezoneOffset() * 60) * 1000;
    return customFormatDateTime(num, format);
}

/**
 * 将秒数转换为 天、时、分、秒数 的元数据
 * @param  {number} seconds 需要转换的秒数
 * @param  {bool} isTotalTime 为true时会向上取整
 * @return {object}
 */
export function getTimeData(seconds: number, isTotalTime = true) {
    const secondsPerMinute = 60;
    const secondsPerHour = 60 * secondsPerMinute;
    const secondsPerDay = 24 * secondsPerHour;

    let second: number;
    // 向上取整
    if (isTotalTime) {
        second = Math.ceil(+seconds) || 0;
    } else {
        second = Math.round(+seconds) || 0;
    }

    const day = Math.floor(second / secondsPerDay);

    second = second - day * secondsPerDay;
    const hour = Math.floor(second / secondsPerHour);

    second = second - hour * secondsPerHour;
    const minute = Math.floor(second / secondsPerMinute);

    second = second - minute * secondsPerMinute;
    return {
        second,
        minute,
        hour,
        day,
    };
}

/**
 * 将秒数转换为本地时间，默认转换为 00:00 格式，将第二个参数设置为true时为 00:00:00 格式
 * @param  {number} seconds   需要转换的秒数
 * @param  {bool}   padToHour 是否转换到小时
 * @return {string}
 */
export function convertSecondsToTimeFormat(seconds: number, padToHour: boolean = true, isTotalTime = true) {
    const data = getTimeData(seconds, isTotalTime);
    const { second, day } = data;

    let { minute, hour } = data;

    hour += day * 24;

    let ret = '';
    if (padToHour) {
        if (Number(hour) >= 100) {
            ret = hour + ':';
        } else {
            ret = pad0(hour) + ':';
        }
    } else {
        minute += hour * 60;
    }
    ret += pad0(minute) + ':' + pad0(second);
    return ret;
}

export function getYYMMDDHHMM(timespan: number) {
    const date = new Date(timespan);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const hour = ('0' + date.getHours()).slice(-2);
    const min = ('0' + date.getMinutes()).slice(-2);
    const ret = month + '-' + day + ' ' + hour + ':' + min;
    if (new Date().getFullYear() === year) {
        return ret;
    } else {
        return year + '-' + ret;
    }
}

/**
 * convertTime，常用在评论列表里
 * 格式化时间为：X月X日(en 为 月/日)、3+天前、前天、昨天、XX小时前、XX分钟前、刚刚
 * @param  {Number} origTime [js时间戳]
 * @return {String}          []
 */
export const getSplitStr = () => {
    if(isLang('CN') || isLang('TW') || isLang('JP')) {
        return '';
    }
    return ' ';
}

export function convertTime(origTime: number, takeTime?: boolean) {
    if (!origTime) {
        return '';
    }
    const date = new Date(origTime);
    const splitStr = getSplitStr()
    const isThisYear = date.getFullYear() === new Date().getFullYear();
    let seconds = 0;
    let minutes = 0;
    let hours = 0;
    let days = 0;
    const todayHours = new Date().getHours();

    seconds = Math.ceil((Date.now() - date.getTime()) / 1000);
    if (seconds > 0) {
        minutes = Math.floor(seconds / 60);
        hours = Math.floor(minutes / 60);
        if (hours > todayHours) {
            days = Math.ceil((hours - todayHours) / 24);
        }
    }

    let ret = '';
    if (days > 7) {
        ret = `${date.getMonth() + 1}${splitStr}${commonLang('月')}${splitStr}${date.getDate()}${splitStr}${commonLang('日')}`;
    } else if (days >= 3) {
        ret = `${days}${splitStr}${commonLang('天前')}`;
    } else if (days === 2) {
        ret = commonLang('前天');
    } else if (days === 1) {
        ret = commonLang('昨天');
    } else if (hours >= 1) {
        ret = commonLang('{$0{0=小时前,1=小时前}}', hours);
    } else if (minutes >= 1) {
        ret = commonLang('{$0{0=分钟前,1=分钟前}}', minutes);
    } else {
        ret = commonLang('刚刚');
    }

    if (days >= 1 && takeTime) {
        const timeHours = date.getHours();
        const timeMinutes = date.getMinutes();
        const time = ` ${timeHours > 9 ? timeHours : '0' + timeHours}:${
            timeMinutes > 9 ? timeMinutes : '0' + timeMinutes
        }`;
        ret += time;
    }

    if (!isThisYear) {
        ret = `${date.getFullYear()}${splitStr}${commonLang('年')}${splitStr}${ret}`;
    }

    return ret;
}

/**
 * 获取today的0点0分0秒
 */
export function getTodayStart(theDate?: Date) {
    const now = !!theDate ? theDate : new Date();
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    return date;
}

/**
 * 获取today的23点59分59秒
 */
export function getTodayEnd(theDate?: Date) {
    const now = !!theDate ? theDate : new Date();
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return date;
}

/**
 * 获取时间
 * @param timeInSeconds linux 时间
 */
export function getTimeObj(timeInSeconds: number) {
    const date = new Date(timeInSeconds * 1000);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    return {
        year,
        month,
        day,
        hours,
        minutes,
        seconds,
    };
}

/**
 * 获取 时分秒
 * @param timeInSeconds 秒
 */
export function getDurationObj(timeInSeconds: number) {
    const date = new Date(timeInSeconds * 1000);
    const result = date.toISOString().substr(11, 8);
    return {
        hours: Number(result.substr(0, 2)),
        minutes: Number(result.substr(3, 2)),
        seconds: Number(result.substr(6, 2)),
    };
}

/**
 * 获取Linux时间戳
 * @param time
 * @returns 秒为单位的时间戳
 */
export const getLinuxTime = (time: number | string) => {
    if (typeof time === 'number') {
        return Math.round(time / 1000);
    }
    return Math.round(new Date(time).getTime() / 1000);
};
